import axios from "axios";
import Cookies from "js-cookie";

const authErrorEvent = new Event("authError");
const apiClient = axios.create({
  baseURL: "https://dashboard.crossguard.senzo.com.my",
  withCredentials: true,
});
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("jwtToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const deleteCookie = (cookieName) => {
  document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
};

let isRefreshing = false;
let failedQueue = [];
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if this is a login endpoint - don't attempt refresh for login failures
    const isLoginEndpoint = originalRequest.url.includes("/auth/login");
    if(
      error.response?.status === 403 && 
      (error.response?.data?.errorMessage?.includes("suspended") || 
      error.response?.data?.message?.includes("suspended") ||
      error.response?.data?.includes("suspended"))
    ) {
      const userSuspendedEvent = new Event("userSuspended");
      document.dispatchEvent(userSuspendedEvent);

      localStorage.removeItem("jwtToken");
      localStorage.removeItem("isAuthenticated");
      Cookies.remove("refreshToken");

      return Promise.reject(error);
    }
    // If it's a 401 from a non-login endpoint and we haven't retried
    if (
      error.response?.status === 401 &&
      !isLoginEndpoint &&
      !originalRequest._retry
    ) {
      // Check if we have a refresh token before attempting refresh
      const refreshToken = Cookies.get("refreshToken");
      if (!refreshToken) {
        // No refresh token available, treat as authentication failure
        document.dispatchEvent(authErrorEvent);
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If we're already refreshing, add this request to queue
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const tokenRequest = {
          AccessToken: null,
          RefreshToken: refreshToken,
        };

        const res = await apiClient.post(
          `${apiBaseUrl}/Auth/refresh`,
          tokenRequest,
          {
            withCredentials: true,
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        const { token } = res.data;

        localStorage.setItem("jwtToken", token);
        apiClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        originalRequest.headers["Authorization"] = `Bearer ${token}`;

        processQueue(null, token);

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        console.log("Refresh token expired");
        localStorage.removeItem("jwtToken");
        localStorage.removeItem("isAuthenticated");
        deleteCookie("refreshToken");

        // Dispatch an event instead of redirecting
        document.dispatchEvent(authErrorEvent);

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // For all other errors (including 401 from login), just reject
    return Promise.reject(error);
  }
);

export default apiClient;
